import { ClipboardIcon, EyeIcon, EyeOffIcon } from 'lucide-react';
import { useState, type FormEvent, type JSX } from 'react';

import { Button } from '@/components/ui/button.js';
import { Input } from '@/components/ui/input.js';
import { Label } from '@/components/ui/label.js';

import { notify } from '@/lib/notify.js';

import { useCredentialAction } from '../../hooks/useCredentialAction.js';
import * as api from '../../services/integration-api.js';

import { useConnectorScope } from './connector-scope.js';

interface FormErrors {
  pat?: string;
  submit?: string;
}

/**
 * GitHub's token form — one field, because a GitHub token is account-scoped and
 * there is nothing else to pin. Where the connector also offers a sign-in, that
 * choice is made in the connection-method dialog before this form opens.
 */
export function GithubCredentialsForm({
  onSaved,
  onCancel,
  details,
  explainUnavailableActions = false,
  onBusyChange,
}: {
  onSaved: () => void;
  onCancel: () => void;
  /**
   * What the new connection should be called and when the agent should reach for it, plus which lane it
   * lands in. Absent on a first connection of the provider — there is nothing to tell it apart from.
   */
  details?: api.ConnectionCreateDetails;
  explainUnavailableActions?: boolean;
  onBusyChange?: (busy: boolean) => void;
}): JSX.Element {
  const [pat, setPat] = useState('');
  const [errors, setErrors] = useState<FormErrors>({});
  const [submitting, setSubmitting] = useState(false);
  const [showPat, setShowPat] = useState(false);
  const [pasteError, setPasteError] = useState<string | null>(null);
  const { workspaceId } = useConnectorScope();
  const action = useCredentialAction(
    explainUnavailableActions,
    `${workspaceId ?? ''}:${details?.connectionId ?? 'new'}`,
    onBusyChange,
  );

  const handlePatChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setPat(e.target.value);
    if (errors.pat) setErrors((es) => ({ ...es, pat: undefined }));
  };

  /** Reads the clipboard into the PAT field on an explicit user gesture. */
  const handlePasteFromClipboard = async (): Promise<void> => {
    if (action.blocked()) return;
    setPasteError(null);
    try {
      const text = await navigator.clipboard.readText();
      if (!text) return;
      setPat(text.trim());
      if (errors.pat) setErrors((es) => ({ ...es, pat: undefined }));
    } catch {
      setPasteError('Clipboard access denied. Paste manually with Cmd/Ctrl+V.');
    }
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    if (action.blocked()) return;
    const token = pat.trim();
    if (token.length < 8) {
      setErrors({ pat: 'Token looks too short' });
      if (explainUnavailableActions)
        notify.error('Enter a valid personal access token before connecting.');
      return;
    }
    const ticket = action.begin();
    if (!ticket) return;
    setSubmitting(true);
    setErrors({});
    try {
      await api.saveGithubCredentials({ pat: token }, workspaceId, details);
      action.complete(ticket, onSaved);
    } catch (err) {
      if (!action.isCurrent(ticket)) return;
      const message =
        err instanceof Error ? err.message : 'Failed to save credentials';
      setErrors({ submit: message });
    } finally {
      if (action.isCurrent(ticket)) setSubmitting(false);
      action.end(ticket);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="mt-3 flex flex-col gap-3"
      noValidate
    >
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="github-pat" className="text-xs">
          Personal Access Token
        </Label>
        <div className="flex items-stretch gap-1.5">
          <div className="relative flex-1">
            <Input
              id="github-pat"
              type={showPat ? 'text' : 'password'}
              autoComplete="new-password"
              spellCheck={false}
              placeholder="GitHub personal access token"
              value={pat}
              onChange={handlePatChange}
              disabled={submitting}
              aria-invalid={Boolean(errors.pat)}
              aria-describedby={
                errors.pat ? 'github-pat-err' : 'github-pat-help'
              }
              className="pr-8"
            />
            <button
              type="button"
              onClick={() => setShowPat((s) => !s)}
              aria-label={showPat ? 'Hide token' : 'Show token'}
              className="absolute right-1.5 top-1/2 -translate-y-1/2 flex size-6 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-foreground/20"
            >
              {showPat ? (
                <EyeOffIcon
                  className="size-3.5"
                  strokeWidth={1.75}
                  aria-hidden="true"
                />
              ) : (
                <EyeIcon
                  className="size-3.5"
                  strokeWidth={1.75}
                  aria-hidden="true"
                />
              )}
            </button>
          </div>
          <button
            type="button"
            onClick={() => void handlePasteFromClipboard()}
            disabled={!explainUnavailableActions && submitting}
            aria-label="Paste from clipboard"
            className="inline-flex h-9 shrink-0 items-center justify-center gap-1 rounded-md border border-input bg-transparent px-2.5 text-xs font-medium text-foreground shadow-elev-1 transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foreground/20"
          >
            <ClipboardIcon
              className="size-3.5"
              strokeWidth={1.75}
              aria-hidden="true"
            />
            Paste
          </button>
        </div>
        {pasteError && (
          <p className="text-[11px] text-destructive">{pasteError}</p>
        )}
        {errors.pat ? (
          <p id="github-pat-err" className="text-xs text-destructive">
            {errors.pat}
          </p>
        ) : (
          <p
            id="github-pat-help"
            className="text-[11px] leading-snug text-muted-foreground"
          >
            On GitHub, open{' '}
            <span className="font-medium">github.com/settings/tokens</span> and
            create a token with the <span className="font-medium">repo</span>{' '}
            and <span className="font-medium">read:org</span> scopes. Paste it
            here.
          </p>
        )}
      </div>
      {errors.submit && (
        <p className="text-xs text-destructive">{errors.submit}</p>
      )}
      <div className="flex items-center justify-end gap-2">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => action.cancel(onCancel)}
          disabled={!explainUnavailableActions && submitting}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          size="sm"
          disabled={!explainUnavailableActions && submitting}
        >
          {submitting ? 'Connecting…' : 'Connect'}
        </Button>
      </div>
    </form>
  );
}
